import _jsx from "@babel/runtime/helpers/jsx";
/* global navigator */
import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FormElementHelp, FormElementLabel, ErrorMessages } from 'reaxl';
import { withFormsy } from 'formsy-react';
import { createChainedFunction } from '@atc/create-chained-function';
import CommonFormComponent from '../hocs/CommonFormComponent';
import cleanFormProps from '../cleanFormProps';
const Textarea = /*#__PURE__*/memo(_ref => {
  let {
    // React doesn't render the cols attribute if it is zero
    cols = 0,
    disabled,
    formElementProps = {},
    isFlat = false,
    isFormDisabled,
    isPristine,
    onBlur,
    onChange,
    rootClasses,
    resizable = true,
    rows = 3,
    setValue,
    validateOn = 'blur',
    value,
    ...rest
  } = _ref;
  const [scrolling, setScrolling] = useState(false);
  const [hasValue, setHasValue] = useState(!!value);
  useEffect(() => {
    setHasValue(!!value);
  }, [value]);
  const handleChange = event => {
    // IE hack to fix error msg showing onload
    if (navigator.userAgent.toLowerCase().indexOf('msie') && event.target.defaultValue === event.target.value) {
      return;
    }
    switch (validateOn) {
      case 'blur':
        setValue(event.currentTarget.value, !isPristine);
        break;
      case 'change':
        setValue(event.currentTarget.value);
        break;
      /* istanbul ignore next */
      default:
    }
  };
  const handleBlur = event => {
    setHasValue(!!event?.target?.value);
    switch (validateOn) {
      case 'blur':
        // We only want to validate on initial pass, or onchange will handle validation
        /* istanbul ignore else */
        if (isPristine) {
          setValue(event.currentTarget.value);
        }
        break;
      /* istanbul ignore next */
      default:
    }
  };
  const componentProps = ['attachToForm', 'detachFromForm', 'displayName', 'errorMessage', 'formElementProps', 'forwardedRef', 'hasValue', 'innerRef', 'inputProps', 'isFormSubmitted', 'isPristine', 'isRequired', 'isValid', 'isValidValue', 'resetValue', 'rootClasses', 'runValidation', 'setValidations', 'setValue', 'showError', 'showRequired', 'validate'];
  const cleanedProps = cleanFormProps(rest, componentProps);
  const containerStyle = {
    resize: resizable ? 'auto' : 'none'
  };
  const {
    help,
    errors,
    showErrors,
    layout,
    labelProps
  } = formElementProps;
  const isInsideLayout = layout === 'inside';
  const elementOnly = /*#__PURE__*/_jsx(React.Fragment, {}, "elementOnly", /*#__PURE__*/React.createElement("textarea", Object.assign({
    "data-cmp": "textArea",
    className: clsx({
      hidden: isFlat,
      'padding-top-4': isInsideLayout
    }, 'form-control')
  }, cleanedProps, {
    value: value,
    onBlur: createChainedFunction(handleBlur, onBlur),
    onChange: createChainedFunction(handleChange, onChange),
    disabled: isFormDisabled || disabled,
    style: containerStyle,
    key: "textarea",
    cols: cols,
    rows: rows,
    onScroll: () => setScrolling(true)
  })), isFlat && /*#__PURE__*/_jsx("div", {}, void 0, value), /*#__PURE__*/_jsx(FormElementHelp, {
    help: help
  }), /*#__PURE__*/_jsx(ErrorMessages, {
    errors: errors,
    showErrors: showErrors
  }));
  if (layout === 'elementOnly') {
    return elementOnly;
  }
  const elementHorizontal = /*#__PURE__*/_jsx("div", {
    className: clsx({
      'col-xs-5': layout === 'horizontal'
    })
  }, "elementHorizontal", elementOnly);
  return /*#__PURE__*/_jsx("div", {
    className: clsx([rootClasses], {
      'form-group-inside': isInsideLayout,
      'has-value': hasValue
    })
  }, void 0, /*#__PURE__*/React.createElement(FormElementLabel, Object.assign({}, labelProps, {
    className: clsx([labelProps?.className], {
      'display-none': scrolling && isInsideLayout
    }),
    key: "formElementLabel",
    layout: layout
  })), layout === 'horizontal' ? elementHorizontal : elementOnly);
});
export default /* #__PURE__ */withFormsy(/* #__PURE__ */CommonFormComponent(Textarea));